/**
 * Canonical price formatting for the platform.
 *
 * Style: Zillow/Ethiopian standard — symbol prefix + grouped thousands
 * with commas (e.g. "Br 1,234,567", "$1,234,567"). Never lakh/crore/Mn.
 */

export type CurrencyCode = 'ETB' | 'USD';

const SYMBOL: Record<CurrencyCode, string> = {
  ETB: 'Br',
  USD: '$',
};

/**
 * Convert an amount from one currency to another using the ETB→USD rate
 * (i.e. how many USD equals 1 ETB — typically ~0.0078).
 */
export function convertAmount(
  amount: number,
  from: CurrencyCode,
  to: CurrencyCode,
  etbToUsdRate: number,
): number {
  if (!amount || from === to) return amount || 0;
  if (from === 'ETB' && to === 'USD') return amount * etbToUsdRate;
  if (from === 'USD' && to === 'ETB') return amount / (etbToUsdRate || 0.0078);
  return amount;
}

/**
 * Full price with grouped thousands. e.g. "Br 25,000,000".
 */
export function formatPrice(
  amount: number | null | undefined,
  fromCurrency: CurrencyCode | string = 'ETB',
  displayCurrency: CurrencyCode = 'ETB',
  etbToUsdRate = 0.0078,
): string {
  if (amount == null || isNaN(Number(amount))) return '—';
  const from = (fromCurrency === 'USD' ? 'USD' : 'ETB') as CurrencyCode;
  const value = convertAmount(Number(amount), from, displayCurrency, etbToUsdRate);
  const rounded = Math.round(value);
  const grouped = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(rounded);
  return `${SYMBOL[displayCurrency]} ${grouped}`;
}

/**
 * Compact Zillow-style price for tight spaces. e.g. "Br 25M", "Br 850K", "$190K".
 */
export function formatPriceCompact(
  amount: number | null | undefined,
  fromCurrency: CurrencyCode | string = 'ETB',
  displayCurrency: CurrencyCode = 'ETB',
  etbToUsdRate = 0.0078,
): string {
  if (amount == null || isNaN(Number(amount))) return '—';
  const from = (fromCurrency === 'USD' ? 'USD' : 'ETB') as CurrencyCode;
  const value = convertAmount(Number(amount), from, displayCurrency, etbToUsdRate);
  const sym = SYMBOL[displayCurrency];
  const abs = Math.abs(value);
  if (abs >= 1_000_000_000) return `${sym} ${(value / 1_000_000_000).toFixed(2).replace(/\.?0+$/, '')}B`;
  if (abs >= 1_000_000) return `${sym} ${(value / 1_000_000).toFixed(2).replace(/\.?0+$/, '')}M`;
  if (abs >= 1_000) return `${sym} ${(value / 1_000).toFixed(0)}K`;
  return `${sym} ${Math.round(value)}`;
}

/**
 * Range formatter for projects: "Br 25M – Br 50M".
 */
export function formatPriceRange(
  min: number | null | undefined,
  max: number | null | undefined,
  fromCurrency: CurrencyCode | string = 'ETB',
  displayCurrency: CurrencyCode = 'ETB',
  etbToUsdRate = 0.0078,
): string {
  if (min == null && max == null) return '—';
  if (min != null && max != null && min !== max) {
    return `${formatPriceCompact(min, fromCurrency, displayCurrency, etbToUsdRate)} – ${formatPriceCompact(max, fromCurrency, displayCurrency, etbToUsdRate)}`;
  }
  const v = min ?? max;
  return `From ${formatPriceCompact(v, fromCurrency, displayCurrency, etbToUsdRate)}`;
}
